<script setup lang="ts">
import { useRouter } from 'vue-router';
import { type VisionProject } from '@/models/RobVision';
import { projectTypeIcon, projectTypeLabel } from '@/helpers/RobvisionHelper';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;
const router = useRouter();

defineProps<{
  projects: VisionProject[];
  archived?: boolean;
  loading?: boolean;
}>();

const projectHeaders = [
  { title: $gettext('Name'), key: 'name', sortable: true },
  { title: $gettext('Type'), key: 'project_type', sortable: true },
  { title: $gettext('Status'), key: 'archived', sortable: false },
];

function openProject(project: VisionProject) {
  router.push({
    name: 'robvision-project',
    params: { projectUuid: project.uuid },
  });
}
</script>

<template>
  <v-card rounded="lg">
    <v-data-table
      :headers="projectHeaders"
      :items="projects"
      :sort-by="[{ key: 'name', order: 'asc' }]"
      :loading="loading"
      :row-props="() => ({ class: 'cursor-pointer' })"
      items-per-page="-1"
      hover
      density="comfortable"
      @click:row="
        (_event: unknown, row: { item: VisionProject }) => openProject(row.item)
      "
    >
      <template #item.name="{ item }">
        <!-- Real link (not just @click:row) for keyboard access and
             middle-click / open-in-new-tab -->
        <router-link
          class="font-weight-medium text-decoration-none text-high-emphasis"
          :to="{
            name: 'robvision-project',
            params: { projectUuid: item.uuid },
          }"
          @click.stop
        >
          {{ item.name }}
        </router-link>
      </template>
      <template #item.project_type="{ item }">
        <v-icon
          :icon="projectTypeIcon(item.project_type)"
          size="small"
          class="mr-2"
        />
        {{ projectTypeLabel(item.project_type) }}
      </template>
      <template #item.archived>
        <v-chip
          size="small"
          :color="archived ? undefined : 'info'"
          density="comfortable"
        >
          {{ archived ? $gettext('Archived') : $gettext('Active') }}
        </v-chip>
      </template>
      <template v-if="$slots['no-data']" #no-data>
        <slot name="no-data" />
      </template>
      <template #bottom></template>
    </v-data-table>
  </v-card>
</template>
